import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import {
  Search,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
// import { BatchRunData } from '../../../src/utils/dashboardMockData';
import { AppContext } from '../../../src/App';
import { DashboardFilters } from '../DashboardFilters';
import { BatchRunData } from '@/Utils/dashboardMockData';
interface Filters {
  searchTerm: string;
  batchStatus: string;
}
interface BatchesTabProps {
  data: batchParams;
  filters: DashboardFilters;
  setFilters: React.Dispatch<React.SetStateAction<DashboardFilters>>
}
interface Batch {
  id: string;
  batchId: string;
  date: string;
  chartType: string;
  total: number;
  aiApproved: number;
  aiSendForCorrection: number;
  auditorApproved: number;
  auditorSentForCorrection: number;
  kantimeUpdated: number;
  status: string;
}
interface BatchSummary {
  totalBatches: number;
  runningBatches: number;
  failedBatches: number;
}
export interface batchParams {
  batches: Batch[],
  summary: BatchSummary
}

export function BatchesTab({ data, filters, setFilters }: BatchesTabProps) {
  const { setCurrentView, setSelectedBatchData, orgConfig } = React.useContext(AppContext);
  // const [searchTerm, setSearchTerm] = React.useState('');
  // const [statusFilter, setStatusFilter] = React.useState('all');
  const [selectedBatch, setSelectedBatch] = React.useState<BatchRunData | null>(null);
  const [showBatchDetail, setShowBatchDetail] = React.useState(false);
  // const [currentPage, setCurrentPage] = React.useState({ currentPage: 1, totalItems: data.batches });
  // useEffect(()=>{
  //   setCurrentPage(prev=>({...prev,totalItems:data.batches}));
  // },[data.batches])

  // Filter and search batches
  // const filteredBatches = React.useMemo(() => {
  //   let filtered = data;

  //   // Status filter
  //   if (statusFilter !== 'all') {
  //     filtered = filtered.filter(batch => batch.status.toLowerCase() === statusFilter);
  //   }

  //   // Search filter
  //   if (searchTerm) {
  //     const term = searchTerm.toLowerCase();
  //     filtered = filtered.filter(batch => 
  //       batch.batchId.toLowerCase().includes(term) ||
  //       batch.domain.toLowerCase().includes(term) ||
  //       batch.program.toLowerCase().includes(term)
  //     );
  //   }

  //   return filtered.sort((a, b) => b.date.getTime() - a.date.getTime());
  // }, [data, searchTerm, statusFilter]);
  // pagination 
  // const itemsPerPage = 10;
  // const totalPages = Math.ceil(currentPage.totalItems.length / itemsPerPage);

  // // Fix: Use currentPage.currentPage instead of currentPage
  // const startIndex = (currentPage.currentPage - 1) * itemsPerPage;
  // const paginatedData = currentPage.totalItems?.slice(startIndex, startIndex + itemsPerPage);
  const handleBatchClick = (batch: BatchRunData) => {
    setSelectedBatch(batch);
    setShowBatchDetail(true);
  };

  const handleBatchRowClick = (batch: Batch) => {
    setSelectedBatchData(batch);
    setCurrentView('batch-detail');
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'Partial':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'Failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Complete':
        return 'default';
      case 'Partial':
        return 'secondary';
      case 'Failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getNewStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'InProgress':
        return <Clock className="w-4 h-4 text-orange-600" />;
      case 'Failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getNewStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'default';
      case 'IN_PROGRESS':
        return 'secondary';
      case 'FAILED':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search batches by ID"
                  value={filters?.searchTerm}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Select value={filters?.batchStatus} onValueChange={(e: string) => setFilters(prev => ({ ...prev, batchStatus: e }))}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">In Progress</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>




          </div>
        </CardContent>
      </Card>

      {/* Batch Runs Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            Last 10 Audit Batches ({Math.min(data?.batches?.length || 0, 10)})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {(data?.batches || []).filter(b => b.status === 'COMPLETED').length} Completed
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {(data?.batches || []).filter(b => b.status === 'PENDING').length} In Progress
            </Badge>
            <Badge variant="destructive" className="text-xs">
              {(data?.batches || []).filter(b => b.status === 'FAILED').length} Failed
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border p-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Batch ID</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Date</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Chart Type</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Total</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">AI Approved</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">AI Send <br />for Correction</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Auditor Approved</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Auditor Send <br />for Correction</TableHead>
                  <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">{orgConfig?.labels?.updated}</TableHead>
                  <TableHead className='text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center'>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(data?.batches) && data.batches.length > 0 ? data.batches.slice(0, 10).map((batch: Batch) => {
                  // Mock AI vs Auditor data - in real app this would come from the batch data
                  // const aiApproved = Math.floor(batch.approved * 0.85); // 85% of approved were AI approved
                  // const auditorApproved = batch.approved - aiApproved;
                  // const aiSentForCorrection = Math.floor(batch.sentForCorrection * 0.75); // 75% of corrections were AI flagged
                  // const auditorSentForCorrection = batch.sentForCorrection - aiSentForCorrection;
                  // const kantimeUpdatedCount = batch.status === 'Complete' ? batch.approved : Math.floor(batch.approved * 0.8);

                  // Convert status to new format
                  const getNewStatus = (oldStatus: string) => {
                    switch (oldStatus) {
                      case 'COMPLETED': return 'COMPLETED';
                      case 'Partial': return 'IN_PROGRESS';
                      case 'FAILED': return 'FAILED';
                      default: return 'IN_PROGRESS';
                    }
                  };

                  const newStatus = getNewStatus(batch.status);

                  return (
                    <TableRow
                      key={batch.id}
                      className="hover:bg-muted/50"
                      onClick={() => handleBatchRowClick(batch)}
                    >
                      <TableCell>
                        <span className="font-mono text-sm text-gray-900">{batch.batchId}</span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-900">{new Date(batch.date).toLocaleDateString('en-US')}</div>
                        <div className="text-xs text-muted-foreground">{(() => {
                          const apiTime = batch?.time;

                          // Attach today's date to make a valid Date object
                          const today = new Date();
                          const dateTime = new Date(
                            `${today.toDateString()} ${apiTime}:00`
                          );

                          // Convert to local time with AM/PM
                          const localTime = dateTime.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          });

                          return localTime
                        })()}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs text-center text-gray-900">{batch.chartType}</Badge>
                      </TableCell>
                      <TableCell className="text-center font-medium  text-gray-900">{batch?.total?.toLocaleString()}</TableCell>
                      <TableCell className="text-center">
                        <span className=" text-center text-blue-700 font-medium">{batch?.aiApproved?.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-orange-700 font-medium">{batch?.aiSendForCorrection?.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-green-700 font-medium">{batch?.auditorApproved?.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-red-700 font-medium">{batch?.auditorSentForCorrection?.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium  text-gray-900">{batch?.kantimeUpdated?.toLocaleString()}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant={getNewStatusBadgeVariant(newStatus)} className="text-xs">
                            {newStatus}
                          </Badge>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }) : <TableRow>
                  <TableCell colSpan={10} className="text-center p-6">
                    No Batches Found
                  </TableCell>
                </TableRow>}
              </TableBody>
            </Table>
          </div>

          {data?.batches?.length > 10 && (
            <div className="mt-4 text-center">
              <span className="text-sm text-muted-foreground">
                Showing 10 most recent batches of {data?.batches?.length || 0} total
              </span>
            </div>
          )}
          {/* {totalPages > 1 && (
                          <div className="flex items-center justify-between px-6 py-4 border-t  border-t">
                            <div className="text-sm text-gray-600">
                              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, currentPage.totalItems.length)} of {currentPage.totalItems.length} eCharts
                            </div>
          
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setCurrentPage(prev=>({...prev,currentPage:Math.max(1, currentPage.currentPage - 1)})); setCurrentPageHarvestRuns(Math.max(1, currentPage.currentPage - 1)) }}
                                disabled={currentPage.currentPage === 1}
                              >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                              </Button>
          
                              <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  const page = i + 1;
                                  return (
                                    <Button
                                      key={page}
                                      variant={currentPage.currentPage === page ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => { setCurrentPage(prev=>({...prev,currentPage:page})); setCurrentPageHarvestRuns(page) }}
                                      className="w-8 h-8 p-0"
                                    >
                                      {page}
                                    </Button>
                                  );
                                })}
                                {totalPages > 5 && (
                                  <>
                                    <span className="text-gray-500">...</span>
                                    <Button
                                      variant={currentPage.currentPage === totalPages ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => { setCurrentPage(prev=>({...prev,currentPage:totalPages})); setCurrentPageHarvestRuns(totalPages) }}
                                      className="w-8 h-8 p-0"
                                    >
                                      {totalPages}
                                    </Button>
                                  </>
                                )}
                              </div>
          
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setCurrentPage(prev=>({...prev,currentPage:Math.min(totalPages, currentPage.currentPage + 1)})); setCurrentPageHarvestRuns(Math.min(totalPages, currentPage.currentPage + 1)) }}
                                disabled={currentPage.currentPage === totalPages}
                              >
                                Next
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )} */}
        </CardContent>
      </Card>

      {/* Batch Detail Dialog */}
      <Dialog open={showBatchDetail} onOpenChange={setShowBatchDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Batch Detail: {selectedBatch?.batchId}
            </DialogTitle>
            <DialogDescription>
              Complete batch information and processing details
            </DialogDescription>
          </DialogHeader>

          {selectedBatch && (
            <div className="space-y-6">
              {/* Batch Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Batch ID:</span>
                        <p className="font-mono">{selectedBatch.batchId}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Date/Time:</span>
                        <p>{format(selectedBatch.date, 'MMM dd, yyyy HH:mm')}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Model Version:</span>
                        <p className="font-mono text-xs">{selectedBatch.modelVersion}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Prompt Version:</span>
                        <p className="font-mono text-xs">{selectedBatch.promptVersion}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Guardrail Profile:</span>
                        <p className="font-mono text-xs">{selectedBatch.guardrailProfile}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <p>{selectedBatch.runDuration} minutes</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Infra Region:</span>
                        <p>{selectedBatch.infraRegion}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Triggered By:</span>
                        <span>{selectedBatch.triggeredBy}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Processing Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Total Processed:</span>
                        <p className="text-lg font-bold">{selectedBatch.totalProcessed.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Approved:</span>
                        <p className="text-lg font-bold text-green-600">{selectedBatch.approved.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sent for Correction:</span>
                        <p className="text-lg font-bold text-orange-600">{selectedBatch.sentForCorrection.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Approval Rate:</span>
                        <p className="text-lg font-bold">
                          {selectedBatch.totalProcessed > 0
                            ? ((selectedBatch.approved / selectedBatch.totalProcessed) * 100).toFixed(1)
                            : 0
                          }%
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Billable Units:</span>
                        <p className="text-lg font-bold text-blue-600">{selectedBatch.billableUnits.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Revenue:</span>
                        <p className="text-lg font-bold text-green-700">{formatCurrency(selectedBatch.totalDueUSD)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Items Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Items ({selectedBatch.items?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Chart ID</TableHead>
                          <TableHead>Visit ID</TableHead>
                          <TableHead>Outcome</TableHead>
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Reviewer</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Array.isArray(selectedBatch.items) && selectedBatch.items.slice(0, 20).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-xs">{item.chartId}</TableCell>
                            <TableCell className="font-mono text-xs">{item.visitId}</TableCell>
                            <TableCell>
                              <Badge
                                variant={item.outcome === 'Approved' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {item.outcome}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              {format(item.timestamp, 'HH:mm:ss')}
                            </TableCell>
                            <TableCell className="text-xs">{item.reviewer || '-'}</TableCell>
                            <TableCell className="text-xs">{item.correctionReason || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {(selectedBatch.items?.length || 0) > 20 && (
                    <div className="mt-2 text-center">
                      <span className="text-xs text-muted-foreground">
                        Showing 20 of {selectedBatch.items?.length || 0} items
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedBatch.reviewerNotes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Reviewer Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedBatch.reviewerNotes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}